import type { MusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import {
  effectParamsOf,
  MUSIC_VIDEO_EFFECTS,
  MUSIC_VIDEO_MAX_EFFECTS,
  type MusicVideoEffect,
  type MusicVideoEffectKind,
} from 'fym-shared/src/music24/musicVideoEffects';

/**
 * The edits of the effects of a cut (spec 2026-10-17 directed video §8). Every function is pure and
 * keeps an effect inside the video (`settings.startSec..endSec`) and inside the length range of its
 * kind (`MUSIC_VIDEO_EFFECTS[kind].minSec..maxSec`). The list stays sorted by start.
 */

/** A new effect that is not a transition lasts this long (less or more when its kind needs it). */
export const NEW_EFFECT_SEC = 4;

/** Kept to the millisecond, as the server parser. */
function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function sortedByStart(effects: MusicVideoEffect[]): MusicVideoEffect[] {
  return effects
    .map((effect, index) => ({ effect, index }))
    .sort((a, b) =>
      a.effect.startSec !== b.effect.startSec ? a.effect.startSec - b.effect.startSec : a.index - b.index
    )
    .map(({ effect }) => effect);
}

/** The length of a new effect: the middle of the range for a transition, else `NEW_EFFECT_SEC` in the range. */
export function defaultEffectLengthSec(kind: MusicVideoEffectKind): number {
  const spec = MUSIC_VIDEO_EFFECTS[kind];
  if (spec.family === 'transition') {
    return round3((spec.minSec + spec.maxSec) / 2);
  }
  return clamp(NEW_EFFECT_SEC, spec.minSec, spec.maxSec);
}

/** A span of `lengthSec` from `startSec`, moved to fit inside the video. Null when the video is too short. */
function placeInVideo(
  doc: MusicVideoDoc,
  startSec: number,
  lengthSec: number
): { startSec: number; endSec: number } | null {
  const { startSec: rangeStart, endSec: rangeEnd } = doc.settings;
  if (rangeEnd - rangeStart < lengthSec) {
    return null;
  }
  const start = clamp(startSec, rangeStart, rangeEnd - lengthSec);
  return { startSec: round3(start), endSec: round3(start + lengthSec) };
}

/**
 * Adds an effect of `kind` at `atSec` (the play head) with the default params of its kind. A
 * transition is centred on `atSec` (it lies across a clip edge); every other kind starts at
 * `atSec`. Nothing changes at `MUSIC_VIDEO_MAX_EFFECTS` or when the video is shorter than the
 * shortest effect of the kind. Pure.
 */
export function addEffect(
  doc: MusicVideoDoc,
  input: { id: string; kind: MusicVideoEffectKind; atSec: number }
): MusicVideoDoc {
  if (doc.effects.length >= MUSIC_VIDEO_MAX_EFFECTS) {
    return doc;
  }
  const spec = MUSIC_VIDEO_EFFECTS[input.kind];
  const lengthSec = Math.min(defaultEffectLengthSec(input.kind), doc.settings.endSec - doc.settings.startSec);
  if (lengthSec < spec.minSec) {
    return doc;
  }
  const from = spec.family === 'transition' ? input.atSec - lengthSec / 2 : input.atSec;
  const times = placeInVideo(doc, from, lengthSec);
  if (times === null) {
    return doc;
  }
  const effect: MusicVideoEffect = { id: input.id, kind: input.kind, ...times, params: effectParamsOf(input.kind, {}) };
  return { ...doc, effects: sortedByStart([...doc.effects, effect]) };
}

function updateEffect(
  doc: MusicVideoDoc,
  id: string,
  update: (effect: MusicVideoEffect) => MusicVideoEffect
): MusicVideoDoc {
  if (!doc.effects.some((effect) => effect.id === id)) {
    return doc;
  }
  return { ...doc, effects: sortedByStart(doc.effects.map((effect) => (effect.id === id ? update(effect) : effect))) };
}

/** Moves an effect so that it starts at `startSec`; its length stays. Pure. */
export function moveEffect(doc: MusicVideoDoc, id: string, startSec: number): MusicVideoDoc {
  return updateEffect(doc, id, (effect) => {
    const times = placeInVideo(doc, startSec, effect.endSec - effect.startSec);
    return times === null ? effect : { ...effect, ...times };
  });
}

/**
 * Sets the start or the end of an effect (inside the video). The other edge stays where it can and
 * moves when the length would leave the range of the kind: a start set after the end pushes the end
 * on, a start set far before it pulls the end in. Nothing changes when the video is shorter than the
 * shortest effect of the kind. Pure.
 */
export function setEffectTimes(
  doc: MusicVideoDoc,
  id: string,
  patch: { startSec?: number; endSec?: number }
): MusicVideoDoc {
  const { startSec: rangeStart, endSec: rangeEnd } = doc.settings;
  return updateEffect(doc, id, (effect) => {
    const spec = MUSIC_VIDEO_EFFECTS[effect.kind];
    if (rangeEnd - rangeStart < spec.minSec) {
      return effect;
    }
    if (patch.startSec !== undefined) {
      const start = clamp(patch.startSec, rangeStart, rangeEnd - spec.minSec);
      const end = clamp(effect.endSec, start + spec.minSec, Math.min(rangeEnd, start + spec.maxSec));
      return { ...effect, startSec: round3(start), endSec: round3(end) };
    }
    if (patch.endSec !== undefined) {
      const end = clamp(patch.endSec, rangeStart + spec.minSec, rangeEnd);
      const start = clamp(effect.startSec, Math.max(rangeStart, end - spec.maxSec), end - spec.minSec);
      return { ...effect, startSec: round3(start), endSec: round3(end) };
    }
    return effect;
  });
}

/**
 * Changes one param of an effect. The value goes through the rules of its kind (`effectParamsOf`).
 * A text keeps its spaces while the user types (the server parser trims it at the save); it is only
 * cut to its longest length. Pure.
 */
export function setEffectParam(doc: MusicVideoDoc, id: string, key: string, value: number | string): MusicVideoDoc {
  return updateEffect(doc, id, (effect) => {
    const params = effectParamsOf(effect.kind, { ...effect.params, [key]: value });
    const spec = MUSIC_VIDEO_EFFECTS[effect.kind].params.find((item) => item.key === key);
    if (spec?.type === 'text' && typeof value === 'string') {
      params[key] = value.slice(0, spec.maxLength);
    }
    return { ...effect, params };
  });
}

export function removeEffect(doc: MusicVideoDoc, id: string): MusicVideoDoc {
  return { ...doc, effects: doc.effects.filter((effect) => effect.id !== id) };
}
